import { useTranslation } from 'react-i18next';

import { Col, Row } from 'antd';
import { motion } from 'framer-motion';

import CountryPreview from 'components/CountryPreview';
import getCityStateNameFromLocation from 'components/Reports/RecruitmentOverviewReport/RecruitmentOverviewReportChart/utils/getCityStateNameFromLocation';

import { WorkExperienceRecord } from 'types/commons';

import { DETAILS_PANEL_ROW_GUTTER, FALLBACK_VALUE } from 'constants/uiSettings';

import styles from '../EducationWorkExperience.module.scss';
import generateTimePeriodString from '../utils/generateDateString';

interface WorkExperienceItemProps {
  record: WorkExperienceRecord;
}

const WorkExperienceItem = ({ record }: WorkExperienceItemProps) => {
  const { t, i18n } = useTranslation();
  const cityState = getCityStateNameFromLocation(record?.location);
  const timePeriodString = generateTimePeriodString({
    startDate: record.startDate,
    endDate: record.endDate,
    t,
    locale: i18n.language,
    isShowDuration: true
  });

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className={styles.tableWrapper}
    >
      <Row className={styles.row} gutter={DETAILS_PANEL_ROW_GUTTER}>
        <Col span={24}>
          <div className={styles.title}>
            {record.jobTitle || FALLBACK_VALUE}
          </div>
        </Col>

        <Col span={24}>
          <div className={styles.subtitle}>
            {record.companyName || FALLBACK_VALUE}
          </div>
        </Col>

        <Col span={24}>
          <div className={styles.location}>
            {record?.location?.country ? (
              <CountryPreview countryCode={record.location.country} />
            ) : (
              FALLBACK_VALUE
            )}

            <span className={styles.value}>{cityState || FALLBACK_VALUE}</span>
          </div>
        </Col>
      </Row>

      <Row className={styles.row} gutter={DETAILS_PANEL_ROW_GUTTER}>
        <Col span={24}>
          <div className={styles.date}>{timePeriodString}</div>
        </Col>
      </Row>

      {record.responsibilities && (
        <Row className={styles.row} gutter={DETAILS_PANEL_ROW_GUTTER}>
          <Col span={24}>
            <div className={styles.responsibilities}>
              {record.responsibilities.split('\n').map((textLine, index) => (
                <span key={index}>{textLine}&nbsp;</span>
              ))}
            </div>
          </Col>
        </Row>
      )}
    </motion.div>
  );
};

export default WorkExperienceItem;
